"use client";

// NettiUiBridge — performs what netti asks the SCREEN to do.
//
// A tool never touches the page. It returns a directive (lib/netti/tools/types
// NettiUi) and the chat hook broadcasts it on the window as
// `innernet:netti-ui` ({ detail: NettiUi }). This one component, mounted once
// inside the dock, hears it and does the thing the person can see:
//
//   open_connect  → the guided connect window for one tool, over the page, the
//                   same ConnectGuideModal the connections list opens. It reads
//                   the person's own connections (the tool may have just minted
//                   one) and, when asked for a phase other than the first, walks
//                   to that chip once the window is up.
//   panel_view    → a panel of the dashboard on one of its tabs. The view is left
//                   in sessionStorage for a panel that has not mounted yet and
//                   announced on `innernet:panel-view` for one that has; then the
//                   shell is carried there.
//   open_import   → the import window (the one settings → data uses).
//   open_tab      → a public page (docs, community, company…) in a NEW tab, so the
//                   dashboard the person is in never goes away.
//   open_web      → a page on the open web (a link row of kind web), in a NEW browser
//                   tab with no opener. https only, no credentials in the address.
//
// Every path is checked here too: only an in-site path ever opens as a site page,
// never a protocol-relative or backslash one, and never an api route. The open web
// has its own door, and it is https alone: the shell re-sends a web row's address
// as open_tab, so an https address that arrives there is handed to that same door.

import * as React from "react";
import dynamic from "next/dynamic";
import { rearmable } from "../os/SafeBoundary";
import { useNext } from "../../lib/release/next";
import { CONNECT_TOOLS, type ConnectTool } from "../../lib/connect/tools";
import type { Connection } from "../../lib/connect/connections";
import type { NettiUi } from "../../lib/netti/tools/types";
import { PANEL_VIEW_IDS, safeWebUrl } from "../../lib/netti/linkActions";
import { CONNECT_SHEET_EVENT, connectWithNetti } from "../../lib/connect/nettiConnect";

// law_snappy: the windows are their own chunks, fetched the first time netti
// opens one (the bridge stands in every dashboard; the windows are rarely asked
// for). Loaded the way the shell loads its rare parts (OsShell): a chunk that
// fails is retried, and one that gave up is re-armed with the rest.
function Quiet() {
  return null;
}
const ConnectGuideModal = rearmable((load) => dynamic(() => load(() => import("../connect/ConnectGuideModal")), { loading: Quiet }));
const ConnectGuideModalV1 = rearmable((load) => dynamic(() => load(() => import("../connect/ConnectGuideModal.v1")), { loading: Quiet }));
const ImportSteps = rearmable((load) => dynamic(() => load(() => import("../connect/ImportSteps")), { loading: Quiet }));
/** the connect window's code, asked for the moment netti asks for the window (it lands while the connections are read) */
function connectWindowCode(next: boolean): Promise<unknown> {
  return (next ? import("../connect/ConnectGuideModal") : import("../connect/ConnectGuideModal.v1")).catch(() => null);
}

export const NETTI_UI_EVENT = "innernet:netti-ui";
export const PANEL_VIEW_EVENT = "innernet:panel-view";
export const panelViewKey = (panel: string) => `innernet:panel-view:${panel}`;

/** an in-site path: starts with one slash, never `//` or `/\` (an off-site url in disguise) */
const SAFE_PATH = /^\/(?![\/\\])/;

/** the panels that take a view, and the views they really have (one table, lib/netti/linkActions) */
const PANEL_VIEWS = PANEL_VIEW_IDS;
const PANELS = new Set(["settings", "connections", "agents", "netti", "you", "memory"]);

/** the chip a phase lives under in the connect window (its own labels) */
const PHASE_CHIP: Record<string, string> = { permissions: "permissions", instructions: "instructions", import: "import" };

interface ConnectRequest {
  tool: ConnectTool;
  phase?: string;
  /** open ON this connection (its settings), when asked for one */
  connection?: Connection;
  connections: Connection[];
  nonce: number;
}

/** netti's phase words → the connect walk's steps (lib/connect/walk) */
const WALK_STEP: Record<string, string> = { connect: "wire", permissions: "trust", instructions: "instructions", import: "import" };

async function readConnections(): Promise<Connection[]> {
  try {
    const res = await fetch("/api/connections", { cache: "no-store" });
    if (!res.ok) return [];
    const data = (await res.json()) as { connections?: Connection[] };
    return data.connections ?? [];
  } catch {
    return []; // offline — the window still opens, on its naming step
  }
}

export default function NettiUiBridge(): React.JSX.Element | null {
  const [connect, setConnect] = React.useState<ConnectRequest | null>(null);
  // the v1.1 connect window is a test account's (lib/release/next)
  const next = useNext();
  const ConnectWindow = next ? ConnectGuideModal : ConnectGuideModalV1;
  // read by the listeners below, which stand for the bridge's whole life
  const nextRef = React.useRef(next);
  React.useEffect(() => { nextRef.current = next; }, [next]);
  const [importing, setImporting] = React.useState(false);
  const nonce = React.useRef(0);

  const refresh = React.useCallback(async () => {
    const rows = await readConnections();
    setConnect((c) => (c ? { ...c, connections: rows } : c));
  }, []);
  // stable handles: the connect window restarts its watch when these change
  const onChanged = React.useCallback(() => { void refresh(); }, [refresh]);
  const onCloseConnect = React.useCallback(() => setConnect(null), []);
  const onCloseImport = React.useCallback(() => setImporting(false), []);

  React.useEffect(() => {
    let alive = true;

    const perform = async (ui: NettiUi) => {
      // 1 — a panel on one of its tabs
      if (ui.panel_view && PANELS.has(ui.panel_view.panel)) {
        const { panel, view } = ui.panel_view;
        const known = PANEL_VIEWS[panel];
        if (known && known.includes(view)) {
          try { sessionStorage.setItem(panelViewKey(panel), view); } catch { /* private mode — the event below still reaches a mounted panel */ }
          window.dispatchEvent(new CustomEvent(PANEL_VIEW_EVENT, { detail: { panel, view } }));
        }
        window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: `/${panel}` } }));
      }

      // 2 — the import window (never two sheets at once)
      if (ui.open_import) {
        setConnect(null);
        setImporting(true);
      }

      // 3 — the connect window for one tool. Where the connect walk lives in the rail
      // (F.nettiConnect), the walk takes it there instead of a sheet over the page.
      if (ui.open_connect) {
        const tool = CONNECT_TOOLS.find((t) => t.id === ui.open_connect!.tool);
        const phase = ui.open_connect.phase;
        if (tool && connectWithNetti({ tool: tool.id, step: phase ? WALK_STEP[phase] ?? null : null, from: "netti" })) {
          // taken by the walk
        } else if (tool) {
          void connectWindowCode(nextRef.current);
          const rows = await readConnections();
          if (!alive) return;
          setImporting(false);
          setConnect({ tool, phase: ui.open_connect.phase, connections: rows, nonce: ++nonce.current });
        }
      }

      // 4 — a public page, beside the dashboard
      if (typeof ui.open_tab === "string" && SAFE_PATH.test(ui.open_tab) && !ui.open_tab.startsWith("/api/")) {
        window.open(ui.open_tab, "_blank", "noopener");
      }

      // 5 — a page on the open web: its own door, https only, never an opener.
      // A web row's address reaches the shell as a nav path and comes back here as
      // open_tab; an https address there is the same thing, so it goes the same way.
      const web = safeWebUrl(ui.open_web) ?? (typeof ui.open_tab === "string" ? safeWebUrl(ui.open_tab) : null);
      if (web) window.open(web, "_blank", "noopener,noreferrer");
    };

    const onUi = (e: Event) => {
      const ui = (e as CustomEvent<NettiUi | undefined>).detail;
      if (ui && typeof ui === "object") void perform(ui);
    };
    // a connection's own settings, asked for from the connect walk ("open its settings")
    const onSheet = async (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (!id) return;
      void connectWindowCode(nextRef.current);
      const rows = await readConnections();
      const conn = rows.find((c) => c.id === id);
      const tool = conn ? CONNECT_TOOLS.find((t) => t.id === conn.tool_id) ?? CONNECT_TOOLS.find((t) => t.id === "other") : null;
      if (!alive || !conn || !tool) return;
      setImporting(false);
      setConnect({ tool, connection: conn, connections: rows, nonce: ++nonce.current });
    };

    window.addEventListener(NETTI_UI_EVENT, onUi);
    window.addEventListener(CONNECT_SHEET_EVENT, onSheet);
    return () => {
      alive = false;
      window.removeEventListener(NETTI_UI_EVENT, onUi);
      window.removeEventListener(CONNECT_SHEET_EVENT, onSheet);
    };
  }, []);

  // asked for a phase other than the first: once the window is up, walk to that
  // chip (the window's own tab). Only when the person already has a connection
  // for this tool: from the naming step the same press would mint one, and
  // netti never makes a door the person did not ask for.
  React.useEffect(() => {
    const chip = connect?.phase ? PHASE_CHIP[connect.phase] : undefined;
    if (!connect || !chip || connect.connections.length === 0) return;
    let tries = 0;
    let timer: ReturnType<typeof setInterval> | undefined;
    let gone = false;
    // the window's code is a chunk of its own: the walk counts from when it can be drawn
    void connectWindowCode(nextRef.current).then(() => {
      if (gone) return;
      timer = setInterval(() => {
        tries += 1;
        const tabs = Array.from(document.querySelectorAll<HTMLElement>('[role="tab"]'));
        const hit = tabs.find((t) => (t.textContent ?? "").trim().toLowerCase() === chip);
        if (hit) {
          clearInterval(timer);
          if (hit.getAttribute("aria-selected") !== "true") hit.click();
        } else if (tries > 20) {
          clearInterval(timer);
        }
      }, 80);
    });
    return () => {
      gone = true;
      clearInterval(timer);
    };
  }, [connect]);

  return (
    <>
      {connect && (
        <ConnectWindow
          key={connect.nonce}
          tool={connect.tool}
          connection={connect.connection ?? null}
          connections={connect.connections.filter((c) => c.tool_id === connect.tool.id)}
          accountName={null}
          onClose={onCloseConnect}
          onChanged={onChanged}
        />
      )}
      {importing && <ImportSteps onClose={onCloseImport} openedFrom="netti" />}
    </>
  );
}
